import React, { useCallback, useState, useRef } from "react";
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  InsertLink,
  InsertPhoto,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
  Undo,
  Redo,
  Code,
  TableChart,
  FormatSize,
  Palette,
  BorderColor,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Divider,
  Menu,
  MenuItem,
  Select,
  Tooltip,
  CircularProgress,
  Popover,
  Switch,
  FormControlLabel,
  Grid,
} from "@mui/material";
import AxiosInstance from "../../utils/axiosInstance";
import showToast from "../Toast/Toster";
import "./EditorToolbar.css";
import InputText from "../InputFields/InputText";

const EditorToolbar = ({ editor, showTable = true, showImage = true }) => {
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  const baseUrl = import.meta.env.VITE_BASE_API;
  
  // List menu states
  const [bulletListAnchor, setBulletListAnchor] = useState(null);
  const [orderedListAnchor, setOrderedListAnchor] = useState(null);
  const [listStartAt, setListStartAt] = useState(1);
  const [listReversed, setListReversed] = useState(false);
  
  // Table menu state
  const [tableAnchor, setTableAnchor] = useState(null);
  const [tableSize, setTableSize] = useState({ rows: 1, cols: 1 });
  
  // Alignment menu state
  const [alignmentAnchor, setAlignmentAnchor] = useState(null);
  
  // Font Size, Color, Highlight menu states
  const [fontSizeAnchor, setFontSizeAnchor] = useState(null);
  const [colorAnchor, setColorAnchor] = useState(null);
  const [highlightAnchor, setHighlightAnchor] = useState(null);

  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);

    if (url === null) {
      return;
    }

    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  const uploadImageToCDN = async (file) => {
    try {
      setUploadingImage(true);
      const formData = new FormData();
      formData.append("files", file);

      const response = await AxiosInstance.post(
        `/v1/cdn/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response?.data?.files?.length > 0) {
        const imagePath = response.data.files[0].filename;
        const imageUrl = `${baseUrl}/v1/cdn/upload/${imagePath}`;
        return imageUrl;
      } else {
        throw new Error("No file returned from CDN");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      showToast.error("Failed to upload image. Please try again.");
      throw error;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageFileSelect = useCallback(async (event) => {
    if (!editor) return;
    
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp", "image/svg+xml"];
    if (!allowedTypes.includes(file.type)) {
      showToast.warning("Please upload a valid image file (JPEG, PNG, GIF, WEBP, or SVG)");
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      showToast.warning("File size too large");
      return;
    }

    try {
      const imageUrl = await uploadImageToCDN(file);
      editor.chain().focus().setImage({ src: imageUrl }).run();
      showToast.success("Image uploaded successfully");
    } catch (error) {
      // Error already handled in uploadImageToCDN
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [editor, baseUrl]);

  const addImage = useCallback(() => {
    if (!editor) return;
    
    // Create file input if it doesn't exist
    if (!fileInputRef.current) {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.style.display = "none";
      input.addEventListener("change", handleImageFileSelect);
      document.body.appendChild(input);
      fileInputRef.current = input;
    }

    // Trigger file picker
    fileInputRef.current.click();
  }, [editor, handleImageFileSelect]);

  const handleBulletListClick = (event) => {
    if (!editor) return;
    setBulletListAnchor(event.currentTarget);
  };

  const handleOrderedListClick = (event) => {
    if (!editor) return;
    // Sync state with current list attributes if list is active
    if (editor.isActive("orderedList")) {
      const attrs = editor.getAttributes("orderedList");
      if (attrs.start) {
        setListStartAt(attrs.start);
      } else {
        setListStartAt(1);
      }
      setListReversed(attrs.reversed || false);
    } else {
      // Reset to defaults if no list is active
      setListStartAt(1);
      setListReversed(false);
    }
    setOrderedListAnchor(event.currentTarget);
  };

  const handleTableClick = (event) => {
    if (!editor) return;
    setTableAnchor(event.currentTarget);
  };

  const handleAlignmentClick = (event) => {
    if (!editor) return;
    setAlignmentAnchor(event.currentTarget);
  };

  const applyBulletStyle = (style) => {
    if (!editor) return;
    
    // Build attributes object
    const attrs = {};
    if (style !== "disc") {
      attrs.type = style;
    }
    
    if (!editor.isActive("bulletList")) {
      // Create new bullet list with style
      if (Object.keys(attrs).length > 0) {
        editor.chain().focus().setBulletList(attrs).run();
      } else {
        editor.chain().focus().setBulletList().run();
      }
    } else {
      // Update existing list attributes
      const { state, dispatch } = editor.view;
      const { $from } = state.selection;
      
      // Find the parent bullet list node
      let listPos = null;
      for (let depth = $from.depth; depth > 0; depth--) {
        const node = $from.node(depth);
        if (node.type.name === "bulletList") {
          listPos = $from.before(depth);
          break;
        }
      }
      
      if (listPos !== null) {
        const tr = state.tr;
        const listNode = state.doc.nodeAt(listPos);
        if (listNode) {
          // Build new attributes - if disc, remove type attribute
          const newAttrs = {};
          if (style !== "disc") {
            newAttrs.type = style;
          }
          tr.setNodeMarkup(listPos, undefined, newAttrs);
          dispatch(tr);
        }
      }
    }
    setBulletListAnchor(null);
  };

  const applyOrderedListStyle = (style, startAt = 1, reversed = false, closeMenu = true) => {
    if (!editor) return;
    
    // Build attributes object
    const attrs = {};
    if (startAt !== 1 && startAt > 0) {
      attrs.start = startAt;
    }
    if (reversed) {
      attrs.reversed = true;
    }
    if (style && style !== "decimal") {
      attrs.type = style;
    }
    
    if (!editor.isActive("orderedList")) {
      // Create new list with attributes
      if (Object.keys(attrs).length > 0) {
        editor.chain().focus().setOrderedList(attrs).run();
      } else {
        editor.chain().focus().setOrderedList().run();
      }
    } else {
      // Update existing list attributes
      const { state, dispatch } = editor.view;
      const { $from } = state.selection;
      
      // Find the parent ordered list node
      let listPos = null;
      let listDepth = null;
      for (let depth = $from.depth; depth > 0; depth--) {
        const node = $from.node(depth);
        if (node.type.name === "orderedList") {
          listPos = $from.before(depth);
          listDepth = depth;
          break;
        }
      }
      
      if (listPos !== null && listDepth !== null) {
        const tr = state.tr;
        const listNode = state.doc.nodeAt(listPos);
        if (listNode) {
          // Build new attributes
          const newAttrs = {};
          
          // Handle start attribute
          if (startAt !== 1 && startAt > 0) {
            newAttrs.start = startAt;
          }
          
          // Handle reversed attribute
          if (reversed) {
            newAttrs.reversed = true;
          }
          
          // Handle type attribute
          if (style && style !== "decimal") {
            newAttrs.type = style;
          }
          
          tr.setNodeMarkup(listPos, undefined, newAttrs);
          dispatch(tr);
        }
      } else {
        // If we can't find the list, try to toggle it off and on with new attributes
        editor.chain().focus().toggleOrderedList().run();
        setTimeout(() => {
          if (Object.keys(attrs).length > 0) {
            editor.chain().focus().setOrderedList(attrs).run();
          } else {
            editor.chain().focus().setOrderedList().run();
          }
        }, 10);
      }
    }
    if (closeMenu) {
      setOrderedListAnchor(null);
    }
  };

  const handleStartAtChange = (value) => {
    const newStartAt = parseInt(value) || 1;
    setListStartAt(newStartAt);
    // If there's an active ordered list, apply the change immediately
    if (editor && editor.isActive("orderedList")) {
      const currentAttrs = editor.getAttributes("orderedList");
      const currentStyle = currentAttrs.type || "decimal";
      applyOrderedListStyle(currentStyle, newStartAt, listReversed, false);
    }
  };

  const handleReversedChange = (checked) => {
    setListReversed(checked);
    // If there's an active ordered list, apply the change immediately
    if (editor && editor.isActive("orderedList")) {
      const currentAttrs = editor.getAttributes("orderedList");
      const currentStyle = currentAttrs.type || "decimal";
      const currentStart = currentAttrs.start || 1;
      applyOrderedListStyle(currentStyle, currentStart, checked, false);
    }
  };

  const insertTableWithSize = (rows, cols) => {
    if (!editor) return;
    editor
      .chain()
      .focus()
      .insertTable({ rows, cols, withHeaderRow: false })
      .run();
    setTableAnchor(null);
  };

  const applyAlignment = (align) => {
    if (!editor) return;
    editor.chain().focus().setTextAlign(align).run();
    setAlignmentAnchor(null);
  };

  if (!editor) {
    return <Box className="rich-text-toolbar" />;
  }

  return (
    <Box className="rich-text-toolbar">
      {/* Group 1: Undo/Redo */}
      <Box sx={{ display: "flex", gap: 0 }}>
        <Tooltip title="Undo">
          <Button
            size="small"
            variant="text"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().chain().focus().undo().run()}
          >
            <Undo fontSize="small" />
          </Button>
        </Tooltip>
        <Tooltip title="Redo">
          <Button
            size="small"
            variant="text"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().chain().focus().redo().run()}
          >
            <Redo fontSize="small" />
          </Button>
        </Tooltip>
      </Box>
      <Divider orientation="vertical" flexItem />
      {/* Group 2: Paragraph/Heading */}
      <Select
        size="small"
        value={
          editor.getAttributes("heading").level ||
          (editor.isActive("paragraph") ? "paragraph" : "")
        }
        onChange={(e) => {
          const value = e.target.value;
          if (value === "paragraph") {
            editor.chain().focus().setParagraph().run();
          } else {
            editor.chain().focus().toggleHeading({ level: parseInt(value) }).run();
          }
        }}
        sx={{ minWidth: 120, height: 32 }}
      >
        <MenuItem value="paragraph">Paragraph</MenuItem>
        <MenuItem value="1">Heading 1</MenuItem>
        <MenuItem value="2">Heading 2</MenuItem>
        <MenuItem value="3">Heading 3</MenuItem>
        <MenuItem value="4">Heading 4</MenuItem>
        <MenuItem value="5">Heading 5</MenuItem>
        <MenuItem value="6">Heading 6</MenuItem>
      </Select>
      <Divider orientation="vertical" flexItem />
      {/* Group 3: Text Formatting (Bold, Italic, Underline) */}
      <Box sx={{ display: "flex", gap: 0 }}>
        <Tooltip title="Bold">
          <Button
            size="small"
            variant="text"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={editor.isActive("bold") ? "is-active" : ""}
          >
            <FormatBold fontSize="small" />
          </Button>
        </Tooltip>
        <Tooltip title="Italic">
          <Button
            size="small"
            variant="text"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={editor.isActive("italic") ? "is-active" : ""}
          >
            <FormatItalic fontSize="small" />
          </Button>
        </Tooltip>
        <Tooltip title="Underline">
          <Button
            size="small"
            variant="text"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={editor.isActive("underline") ? "is-active" : ""}
          >
            <FormatUnderlined fontSize="small" />
          </Button>
        </Tooltip>
      </Box>
      <Divider orientation="vertical" flexItem />
      {/* Group 4: Link and Image */}
      <Box sx={{ display: "flex", gap: 0 }}>
        <Tooltip title="Link">
          <Button
            size="small"
            variant="text"
            onClick={setLink}
            className={editor.isActive("link") ? "is-active" : ""}
          >
            <InsertLink fontSize="small" />
          </Button>
        </Tooltip>
        {showImage && (
          <Tooltip title="Upload Image">
            <Button
              size="small"
              variant="text"
              onClick={addImage}
              disabled={uploadingImage}
            >
              {uploadingImage ? (
                <CircularProgress size={16} />
              ) : (
                <InsertPhoto fontSize="small" />
              )}
            </Button>
          </Tooltip>
        )}
      </Box>
      <Divider orientation="vertical" flexItem />
      {/* Group 5: Lists (Bullet and Ordered) */}
      <Box sx={{ display: "flex", gap: 0 }}>
        <Tooltip title="Bullet List">
          <Button
            size="small"
            variant="text"
            onClick={handleBulletListClick}
            className={editor.isActive("bulletList") ? "is-active" : ""}
          >
            <FormatListBulleted fontSize="small" />
          </Button>
        </Tooltip>
        <Menu
          anchorEl={bulletListAnchor}
          open={Boolean(bulletListAnchor)}
          onClose={() => setBulletListAnchor(null)}
          PaperProps={{
            sx: { minWidth: 120 }
          }}
        >
          <MenuItem 
            onClick={() => applyBulletStyle("disc")}
            sx={{ py: 0.5, fontSize: "13px" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box component="span" sx={{ fontSize: "14px" }}>•</Box>
              <Box component="span" sx={{ fontSize: "13px" }}>Disc</Box>
            </Box>
          </MenuItem>
          <MenuItem 
            onClick={() => applyBulletStyle("circle")}
            sx={{ py: 0.5, fontSize: "13px" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box component="span" sx={{ fontSize: "14px" }}>○</Box>
              <Box component="span" sx={{ fontSize: "13px" }}>Circle</Box>
            </Box>
          </MenuItem>
          <MenuItem 
            onClick={() => applyBulletStyle("square")}
            sx={{ py: 0.5, fontSize: "13px" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box component="span" sx={{ fontSize: "14px" }}>■</Box>
              <Box component="span" sx={{ fontSize: "13px" }}>Square</Box>
            </Box>
          </MenuItem>
        </Menu>
        
        <Tooltip title="Numbered List">
          <Button
            size="small"
            variant="text"
            onClick={handleOrderedListClick}
            className={editor.isActive("orderedList") ? "is-active" : ""}
          >
            <FormatListNumbered fontSize="small" />
          </Button>
        </Tooltip>
        <Menu
          anchorEl={orderedListAnchor}
          open={Boolean(orderedListAnchor)}
          onClose={() => setOrderedListAnchor(null)}
          onClick={(e) => {
            // Don't close menu when clicking inside the input / Switch area
            if (e.target.closest(".cs-form-field") || e.target.closest(".MuiFormControlLabel-root")) {
              e.stopPropagation();
            }
          }}
          PaperProps={{
            sx: { 
              minWidth: 180,
              maxWidth: 200,
              padding: "4px 0"
            }
          }}
        >
          <MenuItem 
            onClick={() => applyOrderedListStyle("decimal", listStartAt, listReversed)}
            sx={{ py: 0.75, fontSize: "13px", justifyContent: "center", minHeight: "36px" }}
          >
            1. 2. 3.
          </MenuItem>
          <MenuItem 
            onClick={() => applyOrderedListStyle("decimal-leading-zero", listStartAt, listReversed)}
            sx={{ py: 0.75, fontSize: "13px", justifyContent: "center", minHeight: "36px" }}
          >
            01. 02. 03.
          </MenuItem>
          <MenuItem 
            onClick={() => applyOrderedListStyle("lower-roman", listStartAt, listReversed)}
            sx={{ py: 0.75, fontSize: "13px", justifyContent: "center", minHeight: "36px" }}
          >
            i. ii. iii.
          </MenuItem>
          <MenuItem 
            onClick={() => applyOrderedListStyle("upper-roman", listStartAt, listReversed)}
            sx={{ py: 0.75, fontSize: "13px", justifyContent: "center", minHeight: "36px" }}
          >
            I. II. III.
          </MenuItem>
          <MenuItem 
            onClick={() => applyOrderedListStyle("lower-alpha", listStartAt, listReversed)}
            sx={{ py: 0.75, fontSize: "13px", justifyContent: "center", minHeight: "36px" }}
          >
            a. b. c.
          </MenuItem>
          <MenuItem 
            onClick={() => applyOrderedListStyle("upper-alpha", listStartAt, listReversed)}
            sx={{ py: 0.75, fontSize: "13px", justifyContent: "center", minHeight: "36px" }}
          >
            A. B. C.
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />
          <Box sx={{ px: 1.5, pb: 1 }}>
            <Box sx={{ mb: 1.5 }}>
              <InputText
                label="Start at"
                type="number"
                size="small"
                value={listStartAt}
                onChange={(e) => handleStartAtChange(e.target.value)}
                onBlur={(e) => handleStartAtChange(e.target.value)}
                inputProps={{ min: 1 }}
                fullWidth
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleStartAtChange(e.target.value);
                  }
                }}
                fieldHeight="36px"
              />
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={listReversed}
                  onChange={(e) => handleReversedChange(e.target.checked)}
                  onClick={(e) => e.stopPropagation()}
                  size="small"
                  sx={{ 
                    "& .MuiSwitch-switchBase": {
                      padding: "4px"
                    }
                  }}
                />
              }
              label="Reversed order"
              sx={{ 
                margin: 0,
                "& .MuiFormControlLabel-label": { 
                  fontSize: "12px",
                  marginLeft: "8px"
                } 
              }}
            />
          </Box>
        </Menu>
      </Box>
      <Divider orientation="vertical" flexItem />
      {/* Group 6: Table */}
      {showTable && (
        <>
          <Tooltip title="Insert Table">
            <Button
              size="small"
              variant="text"
              onClick={handleTableClick}
            >
              <TableChart fontSize="small" />
            </Button>
          </Tooltip>
          <Popover
            open={Boolean(tableAnchor)}
            anchorEl={tableAnchor}
            onClose={() => setTableAnchor(null)}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            PaperProps={{
              sx: { p: 1 }
            }}
          >
            <Box>
              <Grid container spacing={0.3} sx={{ width: 150 }}>
                {Array.from({ length: 10 }, (_, row) =>
                  Array.from({ length: 10 }, (_, col) => (
                    <Grid key={`${row}-${col}`}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          border: "1px solid #e0e0e0",
                          cursor: "pointer",
                          bgcolor: row < tableSize.rows && col < tableSize.cols ? "#2563eb" : "transparent",
                          "&:hover": {
                            bgcolor: "#e3f2fd",
                          },
                        }}
                        onMouseEnter={() => setTableSize({ rows: row + 1, cols: col + 1 })}
                        onClick={() => insertTableWithSize(row + 1, col + 1)}
                      />
                    </Grid>
                  ))
                )}
              </Grid>
              <Box sx={{ mt: 0.5, textAlign: "center", fontSize: "12px", color: "#666" }}>
                {tableSize.rows} x {tableSize.cols}
              </Box>
            </Box>
          </Popover>
          <Divider orientation="vertical" flexItem />
        </>
      )}
      {/* Group 7: Blockquote and Code */}
      <Box sx={{ display: "flex", gap: 0 }}>
        <Tooltip title="Blockquote">
          <Button
            size="small"
            variant="text"
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            className={editor.isActive("blockquote") ? "is-active" : ""}
          >
            <FormatQuote fontSize="small" />
          </Button>
        </Tooltip>
        <Tooltip title="Code Block">
          <Button
            size="small"
            variant="text"
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            className={editor.isActive("codeBlock") ? "is-active" : ""}
          >
            <Code fontSize="small" />
          </Button>
        </Tooltip>
      </Box>
      <Divider orientation="vertical" flexItem />
      {/* Group 8: Text Alignment */}
      <Tooltip title="Text Alignment">
        <Button
          size="small"
          variant="text"
          onClick={handleAlignmentClick}
        >
          {editor.isActive({ textAlign: "left" }) && <FormatAlignLeft fontSize="small" />}
          {editor.isActive({ textAlign: "center" }) && <FormatAlignCenter fontSize="small" />}
          {editor.isActive({ textAlign: "right" }) && <FormatAlignRight fontSize="small" />}
          {editor.isActive({ textAlign: "justify" }) && <FormatAlignJustify fontSize="small" />}
          {!editor.isActive({ textAlign: "left" }) && 
           !editor.isActive({ textAlign: "center" }) && 
           !editor.isActive({ textAlign: "right" }) && 
           !editor.isActive({ textAlign: "justify" }) && (
            <FormatAlignLeft fontSize="small" />
          )}
        </Button>
      </Tooltip>
      <Menu
        anchorEl={alignmentAnchor}
        open={Boolean(alignmentAnchor)}
        onClose={() => setAlignmentAnchor(null)}
        PaperProps={{
          sx: { minWidth: 140 }
        }}
      >
        <MenuItem 
          onClick={() => applyAlignment("left")}
          sx={{ py: 0.5, fontSize: "13px" }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <FormatAlignLeft fontSize="small" />
            <Box component="span" sx={{ fontSize: "13px" }}>Align left</Box>
          </Box>
        </MenuItem>
        <MenuItem 
          onClick={() => applyAlignment("center")}
          sx={{ py: 0.5, fontSize: "13px" }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <FormatAlignCenter fontSize="small" />
            <Box component="span" sx={{ fontSize: "13px" }}>Align center</Box>
          </Box>
        </MenuItem>
        <MenuItem 
          onClick={() => applyAlignment("right")}
          sx={{ py: 0.5, fontSize: "13px" }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <FormatAlignRight fontSize="small" />
            <Box component="span" sx={{ fontSize: "13px" }}>Align right</Box>
          </Box>
        </MenuItem>
        <MenuItem 
          onClick={() => applyAlignment("justify")}
          sx={{ py: 0.5, fontSize: "13px" }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <FormatAlignJustify fontSize="small" />
            <Box component="span" sx={{ fontSize: "13px" }}>Justify</Box>
          </Box>
        </MenuItem>
      </Menu>
      <Divider orientation="vertical" flexItem />
      {/* Group 9: Font Size, Color, Highlight */}
      <Box sx={{ display: "flex", gap: 0 }}>
        <Tooltip title="Font Size">
          <Button
            size="small"
            variant="text"
            onClick={(e) => {
              editor.chain().focus().run();
              setFontSizeAnchor(e.currentTarget);
            }}
            sx={{ 
              minWidth: 32,
              fontWeight: 600,
              fontSize: "14px",
              fontFamily: "Arial, sans-serif"
            }}
          >
            A
          </Button>
        </Tooltip>
        <Menu
          anchorEl={fontSizeAnchor}
          open={Boolean(fontSizeAnchor)}
          onClose={() => setFontSizeAnchor(null)}
          onClick={(e) => {
            // Don't close menu when clicking inside menu items
            if (e.target.closest('.MuiMenuItem-root')) {
              e.stopPropagation();
            }
          }}
          PaperProps={{
            sx: { minWidth: 120 }
          }}
        >
          <MenuItem 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setTimeout(() => {
                editor.chain().focus().unsetFontSize().run();
                setFontSizeAnchor(null);
              }, 50);
            }}
            sx={{ py: 0.5, fontSize: "13px" }}
          >
            Default
          </MenuItem>
          <MenuItem 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setTimeout(() => {
                editor.chain().focus().setFontSize("tiny").run();
                setFontSizeAnchor(null);
              }, 50);
            }}
            sx={{ py: 0.5, fontSize: "10px" }}
          >
            Tiny
          </MenuItem>
          <MenuItem 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setTimeout(() => {
                editor.chain().focus().setFontSize("small").run();
                setFontSizeAnchor(null);
              }, 50);
            }}
            sx={{ py: 0.5, fontSize: "12px" }}
          >
            Small
          </MenuItem>
          <MenuItem 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setTimeout(() => {
                editor.chain().focus().setFontSize("big").run();
                setFontSizeAnchor(null);
              }, 50);
            }}
            sx={{ py: 0.5, fontSize: "18px" }}
          >
            Big
          </MenuItem>
          <MenuItem 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setTimeout(() => {
                editor.chain().focus().setFontSize("huge").run();
                setFontSizeAnchor(null);
              }, 50);
            }}
            sx={{ py: 0.5, fontSize: "24px" }}
          >
            Huge
          </MenuItem>
        </Menu>

        <Tooltip title="Text Color">
          <Button
            size="small"
            variant="text"
            onClick={(e) => {
              editor.chain().focus().run();
              setColorAnchor(e.currentTarget);
            }}
            sx={{ 
              minWidth: 32,
              fontWeight: 600,
              fontSize: "16px",
              color: editor.getAttributes("textStyle")?.color || "#000000"
            }}
          >
            A
          </Button>
        </Tooltip>
        <Menu
          anchorEl={colorAnchor}
          open={Boolean(colorAnchor)}
          onClose={() => setColorAnchor(null)}
          onClick={(e) => {
            // Don't close menu when clicking inside menu items
            if (e.target.closest('.MuiMenuItem-root')) {
              e.stopPropagation();
            }
          }}
          PaperProps={{
            sx: { minWidth: 120 }
          }}
        >
          <MenuItem 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setTimeout(() => {
                editor.chain().focus().setColor("#000000").run();
                setColorAnchor(null);
              }, 50);
            }}
            sx={{ py: 0.5, fontSize: "13px" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 16, height: 16, bgcolor: "#000000", border: "1px solid #ccc", borderRadius: "2px" }} />
              <Box component="span">Black</Box>
            </Box>
          </MenuItem>
          <MenuItem 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setTimeout(() => {
                editor.chain().focus().setColor("#FF0000").run();
                setColorAnchor(null);
              }, 50);
            }}
            sx={{ py: 0.5, fontSize: "13px" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 16, height: 16, bgcolor: "#FF0000", border: "1px solid #ccc", borderRadius: "2px" }} />
              <Box component="span">Red</Box>
            </Box>
          </MenuItem>
          <MenuItem 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setTimeout(() => {
                editor.chain().focus().setColor("#0000FF").run();
                setColorAnchor(null);
              }, 50);
            }}
            sx={{ py: 0.5, fontSize: "13px" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 16, height: 16, bgcolor: "#0000FF", border: "1px solid #ccc", borderRadius: "2px" }} />
              <Box component="span">Blue</Box>
            </Box>
          </MenuItem>
          <MenuItem 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setTimeout(() => {
                editor.chain().focus().setColor("#008000").run();
                setColorAnchor(null);
              }, 50);
            }}
            sx={{ py: 0.5, fontSize: "13px" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 16, height: 16, bgcolor: "#008000", border: "1px solid #ccc", borderRadius: "2px" }} />
              <Box component="span">Green</Box>
            </Box>
          </MenuItem>
          <MenuItem 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setTimeout(() => {
                editor.chain().focus().setColor("#FFFF00").run();
                setColorAnchor(null);
              }, 50);
            }}
            sx={{ py: 0.5, fontSize: "13px" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 16, height: 16, bgcolor: "#FFFF00", border: "1px solid #ccc", borderRadius: "2px" }} />
              <Box component="span">Yellow</Box>
            </Box>
          </MenuItem>
        </Menu>

        <Tooltip title="Highlight">
          <Button
            size="small"
            variant="text"
            onClick={(e) => {
              editor.chain().focus().run();
              setHighlightAnchor(e.currentTarget);
            }}
            sx={{ 
              minWidth: 32,
              fontWeight: 600,
              fontSize: "16px",
              backgroundColor: editor.getAttributes("highlight")?.color ? 
                editor.getAttributes("highlight").color : "transparent"
            }}
          >
            <BorderColor fontSize="small" />
          </Button>
        </Tooltip>
        <Menu
          anchorEl={highlightAnchor}
          open={Boolean(highlightAnchor)}
          onClose={() => setHighlightAnchor(null)}
          PaperProps={{
            sx: { minWidth: 140 }
          }}
        >
          <MenuItem 
            onClick={() => {
              editor.chain().focus().unsetHighlight().run();
              setHighlightAnchor(null);
            }}
            sx={{ py: 0.5, fontSize: "13px" }}
          >
            No Highlight
          </MenuItem>
          <MenuItem 
            onClick={() => {
              editor.chain().focus().setHighlight({ color: "#FFFF00" }).run();
              setHighlightAnchor(null);
            }}
            sx={{ py: 0.5, fontSize: "13px" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 16, height: 16, bgcolor: "#FFFF00", border: "1px solid #ccc", borderRadius: "2px" }} />
              <Box component="span">Yellow</Box>
            </Box>
          </MenuItem>
          <MenuItem 
            onClick={() => {
              editor.chain().focus().setHighlight({ color: "#00FF00" }).run();
              setHighlightAnchor(null);
            }}
            sx={{ py: 0.5, fontSize: "13px" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 16, height: 16, bgcolor: "#00FF00", border: "1px solid #ccc", borderRadius: "2px" }} />
              <Box component="span">Green</Box>
            </Box>
          </MenuItem>
          <MenuItem 
            onClick={() => {
              editor.chain().focus().setHighlight({ color: "#FF00FF" }).run();
              setHighlightAnchor(null);
            }}
            sx={{ py: 0.5, fontSize: "13px" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 16, height: 16, bgcolor: "#FF00FF", border: "1px solid #ccc", borderRadius: "2px" }} />
              <Box component="span">Magenta</Box>
            </Box>
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

export default EditorToolbar;
